import { requireInventoryFloor } from '../../compile/inventory-facet';
import { hasSurfaceTypeValue } from '../../../shared/surface-geometry.js';
import { isEncounterIdentity } from '../../encounter-identity';
import { normalizeInventoryGeneration } from '../../compile/inventory-generation';
import { inventoryDefinition } from '../../inventory/definition.js';
import { ftWallRunSectionMins, ftWallRunSectionMax, ftWallRunPieceCenter } from '../../port/k2-geometry/wall_run_carve.js';
import { json_encode } from '../../php-compat/index.js';
// Placed AOE target expansion, following validate_and_adjust_targets.php's two sweeps.
// The scene must supply physical occupancy and an authoritative placement precheck result.
// Unknown scene facts throw HandBack; there is no guessed target list.
import { handBack, itemField, record, refuse, scalar, upper, type Row } from '../context.js';
// PHP-parity pure geometry the FT3D scene already ships (ft3d/engine/ft3d-shape-core.js is the JS twin
// of includes/functions/janus/encounter/ft3d_shape_core.php; golden-vector tested — see that file's
// header). Reused verbatim rather than re-derived: this is exactly the wall/opening ray test the wall
// gate below needs, and re-deriving it here would be a second copy that could drift from the proven one.
import { insideUnion, segmentHit } from '../../../../../ft3d/engine/ft3d-shape-core.js';
// PHP-parity mirror of includes/functions/janus/core/aoe_propagation_mask.php's volume-reach math
// (ft_aoe_mask_volume_reaches / ft_aoe_mask_point_in_region) — the browser's own propagation-mask reader.
import { aoePropagationVolumeReaches } from '../../../../../ft3d/engine/ft3d-aoe-propagation.js';
import { combatCenter, combatSizeFt, type AbilityPreconditionReaders } from '../../actions/ability-preconditions.js';
import { isSelfDirectionalAoe, selfAoeEdgeSpan, type AoeFootprint, type AoePoint, type AoeSpan } from '../../../shared/aoe-self-origin.js';
import { physicalCreatureBounds } from '../../../shared/physical-creature-body.js';
import { meshesOf } from '../../../../../ft3d/engine/mesh-presentation.js';
import { statusRows, statusPaused } from '../../statuses/expiry.js';
import { computedValue } from '../../../shared/array-types.js';
import { castSkipsSameTeam, sameTeamOrSelf } from '../team-skip-gate.js';
import { paintedWallForPlacement, paintedWallIntersectsTarget } from './painted-wall-cast.js';
import { SHAPES, ftShapeAoeIntersectsTarget, type AoeOccupancy } from '../../../shared/aoe-target-geometry.js';
export { SHAPES, ftShapeAoeIntersectsTarget, type AoeOccupancy } from '../../../shared/aoe-target-geometry.js';
/** ft_shape_object_center() + ft_shape_ray_z() for one row inside a stored FT3D shape. */
export interface AoeWallGatePoint { x: number; y: number; z: number }
type Point = readonly [number, number, number];
const RADIAL_SHAPES = new Set(['sphere', 'circle', 'cylinder', 'cube', 'square']);
export interface PlacedAoeReaders {
  encounter: Row;
  value: (row: Row | null, key: string) => unknown;
  has: (row: Row | null, key: string) => boolean;
  inventoryField: (row: Row | null, key: string) => unknown;
  /** Undefined means the FT3D scene has not proved this placement's aim/range/sight result. */
  placementFailure: (selection: string, caster: Row, ability: Row) => string | null | undefined;
  /** The PHP ft_ground_occupancy_shape result; undefined means that volume is unavailable. */
  occupancy: (row: Row) => AoeOccupancy | null | undefined;
  /** Shared scene reader: fixed mesh bounds and current facing, otherwise the current size-tier standee. */
  casterFootprint?: (row: Row) => AoeFootprint | null;
  hidden: (row: Row) => boolean | undefined;
  /** null means no stored shape. Undefined means the page did not supply that fact. */
  sceneShape: Row | null | undefined;
  /** ft_shape_object_center()+ft_shape_ray_z() for this row against sceneShape. Only read when
   *  sceneShape is non-null and the wall gate actually applies; undefined means unproved. */
  wallGatePoint: (row: Row) => AoeWallGatePoint | undefined;
  /** ft_spatial_row_volume(sceneShape, row) — the 3D collision volume a propagation mask reaches
   *  against. Only read when a propagation mask resolved; undefined means unproved. */
  spatialVolume: (row: Row) => AoeOccupancy | null | undefined;
}
/** `emptyArea`: the refusal is §4's "requires a target" for an area that reached nobody (not a placement veto) — a PLAYER's placed cast
 *  takes it as a cast on empty ground instead (finalizePlacedAoeTargets; owner ruling 27, 2026-10-14). */
export interface PlacedAoeExpansion { targets: Row[]; primary?: Row; refusal?: string; emptyArea?: true }
export interface PlacedAoePlacement { targetId: string; propagation?: unknown; paintedWall?: unknown }

const SELF_RANGE = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';
/** buildFt3dAoeTargetSelection: centered areas use a point, persistent diameter, or a box's rotation axis. */
export function centeredPlacedAoePoint(shape: string | undefined, persistent: boolean | undefined, coords: readonly number[]):
  { x: number; y: number; z: number } | null {
  if (!RADIAL_SHAPES.has(shape ?? '') || typeof persistent !== 'boolean' || !coords.every(Number.isFinite)) return null;
  if (coords.length === 3 && !persistent) return { x: coords[0]!, y: coords[1]!, z: coords[2]! };
  if (coords.length !== 6 || (!persistent && shape !== 'cube' && shape !== 'square')) return null;
  return { x: (coords[0]! + coords[3]!) / 2, y: (coords[1]! + coords[4]!) / 2, z: (coords[2]! + coords[5]!) / 2 };
}
/** New cast geometry only: the same size reader as movement/range, and the page's lean position reader. */
type SelfAoeReaders = Pick<AbilityPreconditionReaders, 'value' | 'inventoryField'> & Partial<Pick<PlacedAoeReaders, 'casterFootprint'>>
  & { statuses?: (row: Row) => Row[] };
export function casterAoeFootprintFt(read: SelfAoeReaders, caster: Row, facing = 0): AoeFootprint {
  const form = (read.statuses?.(caster) ?? statusRows(caster, 'target')).find(status => !statusPaused(status)
    && String(itemField(record(status.status_arr) ?? status, 'item_type')) === 'creature');
  const shifted = Number(scalar(caster.shapeshifted) ?? 0) === 1 || !!form;
  const source = shifted ? record(form?.status_arr) : caster;
  const body = source ? physicalCreatureBounds(meshesOf(source as never)) : null;
  if (body) return { kind: 'oblong', halfW: body.width / 2, halfD: body.depth / 2, facing };
  const width = combatSizeFt(caster, read as AbilityPreconditionReaders);
  if (width === null) handBack('self-directional-aoe:caster-footprint-not-held');
  return { kind: 'circle', r: width! / 2 };
}
export function placedAoeSpan(read: SelfAoeReaders, caster: Row, ability: Row,
  shape: string, origin: AoePoint, endpoint: AoePoint): AoeSpan {
  const self = upper(read.value(ability, 'range')) === SELF_RANGE;
  if (!isSelfDirectionalAoe(self, shape)) return selfAoeEdgeSpan(false, shape, origin, 0, origin, endpoint);
  if (Math.hypot(endpoint.x - origin.x, endpoint.y - origin.y) <= 1e-9) handBack('self-directional-aoe:aim-not-held');
  const center = combatCenter(caster);
  if (!center) handBack('self-directional-aoe:caster-position-not-held');
  const footprint = read.casterFootprint ? read.casterFootprint(caster) : casterAoeFootprintFt(read, caster);
  if (!footprint) handBack('self-directional-aoe:caster-footprint-not-held');
  return selfAoeEdgeSpan(true, shape, center!, footprint!, origin, endpoint);
}
/** The page's placement wire, normalized with the same body/facing reader as target expansion. */
export function placedAoeSelectionSpan(read: SelfAoeReaders, selection: string, caster: Row, ability: Row) {
  const shape = SHAPES[upper(read.value(ability, 'aoe_type'))];
  if (!shape) handBack('validate_and_adjust_targets:unknown-aoe-shape');
  const point = selection.split(',')[0]?.trim() ?? '';
  const [encounterId, rawCoords, ...extra] = point.split('|');
  if (extra.length || !isEncounterIdentity(encounterId) || !rawCoords) handBack('validate_and_adjust_targets:invalid-placement');
  const parts = rawCoords!.split(':');
  if (parts.length !== 3 && parts.length !== 6) handBack('validate_and_adjust_targets:unmodelled-coordinates');
  const coords = parts.map(v => num(v, 'validate_and_adjust_targets:invalid-coordinate'));
  const span = placedAoeSpan(read, caster, ability, shape!, { x: coords[0]!, y: coords[1]!, z: coords[2]! },
    { x: coords[3] ?? coords[0]!, y: coords[4] ?? coords[1]!, z: coords[5] ?? coords[2]! });
  return { point, encounterId, span, shape: shape! };
}
/** Retired janus.php's ground-area dispatch (ca5bcbb11:2058–2070).
 * Attack / OA abilities swap to a weapon before validation (ca5bcbb11:524–540); passive dispatch requires a trigger frame.
 * Keep this leaf set aligned with GROUND_AREA_ABILITY_TYPES by test, without importing its dispatch graph into the scene bundle. */
const FINALIZABLE_TYPES = new Set(['spell', 'curse', 'full_ability', 'ability', 'quick_ability', 'free_ability', 'reaction_ability',
  'lair_ability', 'trap_ability', 'menta', 'sanguine', 'potion', 'coating', 'food', 'drink', 'elixir']);
const num = (value: unknown, reason: string): number => {
  const raw = Array.isArray(value) ? value[0] : value;
  if (raw === null || raw === undefined || raw === '' || !Number.isFinite(Number(raw))) handBack(reason);
  return Number(raw);
};
const truth = (value: unknown): boolean => value !== null && value !== undefined && value !== false && value !== '' && value !== 0 && value !== '0';
const entityId = (r: Row, read: PlacedAoeReaders): string => upper(record(r.encounter_creatures)?.encounter_creature_id ?? read.inventoryField(r, 'inventory_id'));
const targetItemType = (read: PlacedAoeReaders, row: Row): string => String(itemField(row, 'item_type')
  ?? itemField(inventoryDefinition(row, read.encounter.catalog), 'item_type') ?? '');
const rows = (value: unknown): Row[] => Array.isArray(value) ? value.filter((r): r is Row => record(r) !== null) : [];

/** SwarmTemplate ids (additions.php). ft_swarm_template_row() finds a swarm by its creature_template entry. */
const SWARM_TEMPLATE_IDS = ['C721A8A3-C301-AE2B-B388-D73FE51F65CC', 'B93C548F-95A2-14A0-129C-C6D40B38C7CB',
  'BD05AC4E-43DC-BC8F-E5A9-D9AA454A9D2B', '1BE8C9F6-F3E8-2618-7FCC-1956C6C15139'];
/** Could ft_swarm_occupied_width_ft() widen this row? A swarm layer, or any SwarmTemplate id anywhere in its creature_template
 *  (bucket or legacy root slot) — a text match, so no entry shape can hide one. The widening itself is not ported. */
export function mayBeSwarm(row: Row, has: (row: Row | null, key: string) => boolean): boolean {
  if (has(row, 'swarm')) return true;
  const text = upper(JSON.stringify([record(row.item_layer_types)?.creature_template ?? null, row.creature_template ?? null]));
  return SWARM_TEMPLATE_IDS.some((id) => text.includes(id));
}

/**
 * PHP ft_apply_self_aoe_size_to_cast_item (combat_distance.php) — the first thing validate_and_adjust_targets does:
 * a centered Self area grows by the caster's size credit (max(0, (width − 5) / 2), doubled for cube / square), and
 * both sweeps read that computed aoe_distance. The width is ft_combat_range_object_size_ft() (combatSizeFt; a
 * sizeless caster earns no credit); its swarm widening (ft_swarm_occupied_width_ft) is not ported.
 */
export function selfAoeDistance(read: Pick<PlacedAoeReaders, 'value' | 'has'>, caster: Row, ability: Row, shape: string, authored: number): number {
  if (upper(read.value(ability, 'range')) !== SELF_RANGE || !RADIAL_SHAPES.has(shape)) return authored;
  if (record(ability.computed)?.ft_self_aoe_size_adjusted) return authored;
  if (mayBeSwarm(caster, read.has)) handBack('ft_apply_self_aoe_size_to_cast_item:swarm-size-not-held');
  const width = combatSizeFt(caster, read as unknown as AbilityPreconditionReaders);
  const credit = width === null ? 0 : Math.max(0, (width - 5) / 2);
  return Math.max(0, authored) + (shape === 'cube' || shape === 'square' ? credit * 2 : credit);
}

/** PHP ft_shape_wall_blocks_line (ft3d_shape_core.php): a demonstrable wall between two floor points. */
function wallBlocksLine(shape: Row, x1: number, y1: number, x2: number, y2: number, z: number): boolean {
  // insideUnion/segmentHit take the raw decoded shape blob (boxes/openings/sceneWalls) — the same JSON
  // schema PHP validates in ft_shape_validate(); no local re-shaping needed. (Untyped JS import.)
  const blob = shape as any; // eslint-disable-line @typescript-eslint/no-explicit-any
  if (!insideUnion(blob, x1, y1) || !insideUnion(blob, x2, y2)) return false;
  return segmentHit(blob, x1, y1, x2, y2, { z, mode: 'sight', containToUnion: true }) !== null;
}

/**
 * PHP ft_shape_aoe_target_blocked (ft3d_shape_core.php). RADIAL forms only — callers must pass
 * `shape = null` for line/cone (the PHP caller nulls $ft_shape_expand/$ft_shape_hidden for those,
 * since a midpoint ray is not their line of effect).
 */
function shapeAoeTargetBlocked(read: PlacedAoeReaders, shape: Row | null, centerX: number, centerY: number, row: Row): boolean {
  if (shape === null) return false;
  const point = read.wallGatePoint(row);
  if (point === undefined) handBack('ft_shape_aoe_target_blocked:scene-height-missing');
  if (![point.x, point.y, point.z, centerX, centerY].every(Number.isFinite))
    handBack('ft_shape_aoe_target_blocked:nonfinite-endpoint');
  return wallBlocksLine(shape, centerX, centerY, point.x, point.y, point.z);
}

/**
 * PHP ft_aoe_propagation_mask (aoe_propagation_mask.php): identity-matches the request-only wire the
 * scene posted for THIS cast (caster/ability/point) and hands back its validated mask, or null when no
 * wire matched (the ordinary case — no propagation mask, fall back to the plain shape gate).
 */
function resolveAoePropagationMask(propagation: unknown, casterId: string, abilityId: string, point: string): Row | null {
  const wire = record(propagation);
  if (!wire) return null;
  if (upper(wire.casterId) !== casterId || upper(wire.abilityId) !== abilityId) return null;
  const wireSelection = String(wire.targetSelection ?? '');
  if (upper(wireSelection.split(',')[0]?.trim() ?? '') !== upper(point)) return null;
  const mask = record(wire.mask);
  if (!mask || mask.version !== 1 || !Array.isArray(mask.barriers) || !record(mask.region)) {
    handBack('ft_aoe_propagation_mask:malformed');
  }
  return mask;
}

/**
 * ft_aoe_mask_target_blocked's wall branch: a room-generated wall run (ft_inventory_bound_wall_run — the row's own generation record; an
 * encounter row always carries it when set, so none means a hand-placed wall) is judged at the point of its run nearest the mask source,
 * on the wall plane, with no radius — its near face, which a zero-thickness structural barrier on that plane cannot block.
 */
function wallRunMaskVolume(read: PlacedAoeReaders, mask: Row, row: Row): AoeOccupancy | null {
  const run = record(normalizeInventoryGeneration(read.inventoryField(row, 'inventory_generation'))?.wallRun);
  if (!run) return null;
  const span = record(run.span)!, z = record(run.z)!, source = record(mask.source);
  const at = Number(run.at), min = Number(span.min), max = Number(span.max);
  const along = Math.max(min, Math.min(max, Number(run.axis === 'x' ? source?.y : source?.x)));
  if (!Number.isFinite(along)) handBack('ft_aoe_propagation_mask:malformed');
  return { kind: 'circle', x: run.axis === 'x' ? at : along, y: run.axis === 'y' ? at : along, r: 0, zMin: Number(z.min), zMax: Number(z.max) };
}

/** PHP ft_aoe_mask_target_blocked (aoe_propagation_mask.php). */
function aoeMaskTargetBlocked(read: PlacedAoeReaders, mask: Row, shape: Row | null, row: Row): boolean {
  if (shape === null) return false;
  const id = entityId(row, read);
  if (id === '') return false; // Preserve the area anchor.
  const volume = (targetItemType(read, row) === 'wall' ? wallRunMaskVolume(read, mask, row) : null) ?? read.spatialVolume(row);
  if (volume === undefined) handBack('ft_spatial_row_volume:scene-volume-missing');
  if (volume === null) return true;
  return !aoePropagationVolumeReaches(mask as any, volume as any, id); // eslint-disable-line @typescript-eslint/no-explicit-any
}

const finite = (value: unknown): number | null => {
  const v = Array.isArray(value) ? value[0] : value;
  return v === null || v === undefined || v === '' || typeof v === 'boolean' || !Number.isFinite(Number(v)) ? null : Number(v);
};
/** pos_coord() (`position`, else the legacy `pos_arr`), then a lean row's own encounter_creatures / inventory columns: a PHP-compiled member
 *  row always carried `position`; the browser's lean member rows may keep the same coordinates only in their columns (ability-truth.ts
 *  coordsOf reads them the same way), so the fallback is that row's position, not a second source. */
const rowCoord = (read: PlacedAoeReaders, row: Row, key: 'x_pos' | 'y_pos' | 'z_pos'): number =>
  finite(record(row.position)?.[key]) ?? finite(record(row.pos_arr)?.[key]) ?? finite(record(row.encounter_creatures)?.[key])
    ?? finite(read.inventoryField(row, key)) ?? 0;

/** validate_and_adjust_targets (after the mask re-filter): a DIRECTIONAL mask (line / cone propagation) usort()s EVERY assembled target by its
 *  projection on the mask direction from its source — ft_shape_object_center() x / y and pos_coord z_pos; the area anchor (no entity id) first.
 *  PHP 8's usort and Array#sort are both stable, so equal projections keep assembly order. (Ported 2026-10-13 — lines / cones indoors.) */
function orderAlongMaskDirection(read: PlacedAoeReaders, mask: Row, targets: Row[]): void {
  const point = (value: unknown): [number, number, number] | null => {
    const p = record(value);
    const xyz = p ? [finite(p.x), finite(p.y), finite(p.z)] : [];
    return xyz.length === 3 && xyz.every((v) => v !== null) ? xyz as [number, number, number] : null;
  };
  const source = point(mask.source), direction = point(mask.direction);
  if (!source || !direction) handBack('ft_aoe_propagation_mask:malformed');
  const [sx, sy, sz] = source!, [dx, dy, dz] = direction!;
  const along = (row: Row): number => !entityId(row, read) ? -Infinity
    : (rowCoord(read, row, 'x_pos') - sx) * dx + (rowCoord(read, row, 'y_pos') - sy) * dy + (rowCoord(read, row, 'z_pos') - sz) * dz;
  const keyed = targets.map((row) => ({ row, at: along(row) }));
  keyed.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
  targets.splice(0, targets.length, ...keyed.map((k) => k.row));
}

const FRIEND_TEAM = '7DBEB6D4-D147-703A-022E-05B290BB95C3', ENEMY_TEAM = '9399856B-2132-7574-1684-91139A2831DD';
/** encounter_row_team_uuid(): the encounter_creatures team (a [0]-wrapped / item-shell value unwrapped), else the row's own team, else the
 *  team layer — an array (no scalar) is no team. */
function encounterRowTeamUuid(read: PlacedAoeReaders, row: Row): string | null {
  const unwrap = (v: unknown): unknown => { while (Array.isArray(v)) v = v[0]; const shell = record(v); return shell && record(shell.items) ? record(shell.items)!.item_id : v; };
  let t = unwrap(record(row.encounter_creatures)?.team);
  if (t === undefined || t === null) t = unwrap(row.team);
  if (t === undefined || t === null) t = unwrap(read.value(row, 'team'));
  return t === null || t === undefined || t === '' || typeof t === 'object' ? null : String(t);
}
/** check_opposing_team(): both rows on the friend / enemy teams, and different ones. */
function opposingTeams(read: PlacedAoeReaders, caster: Row, row: Row): boolean {
  const a = encounterRowTeamUuid(read, caster), b = encounterRowTeamUuid(read, row);
  const valid = (t: string | null): boolean => t === FRIEND_TEAM || t === ENEMY_TEAM;
  return valid(a) && valid(b) && a !== b;
}

/** PHP validate_and_adjust_targets point expansion and hidden-target sweep, retaining PHP row order. */
export function expandPlacedAoeTargets(read: PlacedAoeReaders, placed: string | PlacedAoePlacement, caster: Row, ability: Row,
  /** validate_and_adjust_targets' $auto_expand_caster_in_point_aoe (a destruction payload): the caster is judged by the same geometry. */
  includeCaster = false,
  /** lane A9 (R-130): a TRIGGER frame's selection ([the owner]) is already held — the expansion only ADDS, so an empty area is no empty-area
   *  branch (validate_and_adjust_targets §2 / §4 read the whole target list, which keeps the owner) */
  selectionHeld = false, wallSections?: Map<string, number[]>): PlacedAoeExpansion {
  const placement = typeof placed === 'string' ? placed : placed.targetId;
  requireInventoryFloor(read.encounter);
  const painted = typeof placed === 'string' ? null : paintedWallForPlacement(read, placed, entityId(caster, read), ability);
  const { point, encounterId, span, shape } = placedAoeSelectionSpan(read, placement, caster, ability);
  const origin: Point = [span.origin.x, span.origin.y, span.origin.z], end: Point = [span.endpoint.x, span.endpoint.y, span.endpoint.z];
  // A painted stage has already proved its path. The ordinary straight-line proof does not describe a bent ribbon.
  const failure = painted ? null : read.placementFailure(placement, caster, ability);
  if (failure === undefined) handBack('ft_aoe_placement_failure:scene-proof-missing');
  if (failure !== null) {
    // Validation veto happens before either sweep. The parser has already materialized the map
    // marker followed by each explicit UUID, and PHP leaves that array unchanged on refusal.
    const all = [...rows(read.encounter.encounter_creatures_arr), ...rows(read.encounter.encounter_inventories_arr)];
    const byId = new Map(all.map((r) => [entityId(r, read), r]));
    const initial: Row[] = [{ encounters: { encounter_id: encounterId } }];
    for (const id of placement.split(',').slice(1).map(upper).filter(Boolean)) {
      const row = byId.get(id);
      if (!row) handBack('validate_and_adjust_targets:explicit-row-missing');
      initial.push(row);
    }
    return { targets: initial, refusal: failure };
  }
  const casterId = entityId(caster, read);
  const selfDirectional = isSelfDirectionalAoe(upper(read.value(ability, 'range')) === SELF_RANGE, shape);
  const casterAllowed = (row: Row): boolean => entityId(row, read) !== casterId || (includeCaster && !selfDirectional);
  // PHP resolves the propagation mask (aoe_propagation_mask.php) BEFORE anything below reads it; a
  // resolved mask governs EVERY shape's wall gate, even line/cone (unlike the plain shape gate, which
  // is radial-only). No mask (the ordinary case) falls back to the plain shape gate.
  const abilityId = upper(itemField(ability, 'item_id') as string);
  const mask = painted || typeof placed === 'string' ? null : resolveAoePropagationMask(placed.propagation, casterId, abilityId, point);
  const wallGateApplies = mask !== null || RADIAL_SHAPES.has(shape);
  if (wallGateApplies && read.sceneShape === undefined) handBack('ft_encounter_shape:scene-shape-missing');
  const wallGateShape = wallGateApplies ? read.sceneShape ?? null : null;
  // lane A9 (R-130 instant areas): validate_and_adjust_targets' point expansion reads effect_only_affects_{opposing,same}_team_type ONLY for a
  // persistent area (persistent_aoe_team_type_allows_target) — a non-persistent area collects every creature it reaches and the effect gate
  // filters later (is_target_valid_for_effect, the pipeline's own twin). Real-PHP proof: …-2026-10-02-a9aoe (Battle Call, Beacon of Compassion …).
  const persistent = truth(read.value(ability, 'persistent_aoe'));
  // The point expansion's target filters (ported 2026-10-13 — they used to hand back 'ability-target-filter'): janus_ability_or_rider_flag_active
  // reads the cast item and its attack_ability_arr rider; the persistent team gate is persistent_aoe_team_type_allows_target (no zone row yet);
  // target_opposing_team_only / target_friendly_only screen creatures in BOTH sweeps; opposing-only drops every prop sweep and, at the end, every
  // non-opposing creature and prop however it arrived (the explicit stage occupants included) — see filterOpposingOnly below.
  const riderFlag = (key: string): boolean => truth(read.value(ability, key)) || truth(read.value(record(ability.attack_ability_arr), key));
  const onlyInventory = riderFlag('ability_only_affects_inventory');
  const opposingOnly = truth(read.value(ability, 'target_opposing_team_only'));
  const friendlyOnly = truth(read.value(ability, 'target_friendly_only'));
  const zoneOpposingOnly = persistent && riderFlag('effect_only_affects_opposing_team_type');
  const zoneSameOnly = persistent && riderFlag('effect_only_affects_same_team_type');
  const casterTeam = encounterRowTeamUuid(read, caster) ?? '';
  const creatureFilters = (row: Row): boolean => {
    const opposing = opposingTeams(read, caster, row);
    return !(zoneOpposingOnly && !opposing) && !(zoneSameOnly && opposing) && !(opposingOnly && !opposing)
      && !(friendlyOnly && (encounterRowTeamUuid(read, row) ?? '') !== casterTeam);
  };
  // Wizard v9 aoe_spells_spare_allies (owner 2026-10-09 "allies only"): the cast-local target_skip_same_team stamp (caster-spell-layers.ts)
  // drops the caster and its team from both sweeps and from any named selection; neutral creatures and props in the area are still reached,
  // and the Self-range empty fallback never re-adds the spared caster. (Careful's opposing-only stamp is target_opposing_team_only — filtered below.)
  const skipSameTeam = castSkipsSameTeam(ability);
  const teamAllows = (row: Row): boolean => !skipSameTeam || !sameTeamOrSelf(caster, row, (r, k) => read.value(r, k));
  if (truth(read.value(ability, 'caster_moves_to_end_point_of_aoe_effect_after_cast'))
      || truth(read.value(ability, 'caster_moves_towards_target_in_ft')))
    handBack('validate_and_adjust_targets:retained-map-marker');
  if (truth(read.value(ability, 'aoe_follows_target'))) handBack('search_aoe_primary_target:follow-zone');
  const selfCentered = upper(read.value(ability, 'range')) === SELF_RANGE && RADIAL_SHAPES.has(shape);
  const radius = selfAoeDistance(read, caster, ability, shape, num(read.value(ability, 'aoe_distance'), 'validate_and_adjust_targets:distance-missing'));
  const depth = read.value(ability, 'aoe_distance_depth') == null ? 1 : num(read.value(ability, 'aoe_distance_depth'), 'validate_and_adjust_targets:depth');
  const height = read.value(ability, 'aoe_distance_height') == null ? 1 : num(read.value(ability, 'aoe_distance_height'), 'validate_and_adjust_targets:height');
  const creatures = rows(read.encounter.encounter_creatures_arr);
  const floor = rows(read.encounter.encounter_inventories_arr);
  // A physical floor surface reacts through its footprint. It is never a destructible damage recipient.
  const reactionScenery = (row: Row): boolean => !!read.inventoryField(row, 'inventory_id') && read.has(ability, 'damage_type')
    && hasSurfaceTypeValue(read.value(inventoryDefinition(row, read.encounter.catalog), 'surface_type'));
  const inventories = floor.filter(row => Number(read.inventoryField(row, 'inventory_deleted') ?? 0) !== 1 && !reactionScenery(row));
  const byId = new Map([...creatures, ...floor].map((r) => [entityId(r, read), r]));
  const explicitIds = placement.split(',').slice(1).map(upper).filter(Boolean);
  const targets: Row[] = [];
  const intersects = (row: Row): boolean => {
    if (painted) return paintedWallIntersectsTarget(read, painted, row);
    const o = read.occupancy(row);
    if (o === undefined) handBack('ft_ground_occupancy_shape:scene-volume-missing');
    return o !== null && ftShapeAoeIntersectsTarget(o, shape, radius, depth, height, origin, end, selfCentered);
  };
  // validate_and_adjust_targets.php re-filters EVERY assembled target (explicit ids included) through
  // ft_aoe_mask_target_blocked() ("apply the same mask to explicitly submitted occupants"); the sweeps below apply it as they go, so an
  // explicit id is judged here, in place (an interior room's walls resolve a mask for nearly every placed area — Fog Cloud, 2026-10-13).
  for (const id of explicitIds) {
    const row = byId.get(id);
    if (!row) handBack('validate_and_adjust_targets:explicit-row-missing');
    if (Number(read.inventoryField(row, 'inventory_deleted') ?? 0) === 1) continue;
    if (reactionScenery(row)) continue;
    if ((!selfDirectional || entityId(row, read) !== casterId) && (!painted || intersects(row))
        && (mask === null || !aoeMaskTargetBlocked(read, mask, wallGateShape, row))) targets.push(row);
  }
  // ft_shape_aoe_target_blocked()/ft_aoe_mask_target_blocked() attack-time wall veto: PHP calls this
  // right after the planar hit test passes, in BOTH sweeps below, subtracting a target a wall/mask
  // demonstrably blocks. get_target_point_x()'s and ft_shape_aoe_center_y()'s midpoint-of-origin/end
  // convention — matches every shape, since directional origin===end collapses to the same point.
  const centerX = (origin[0] + end[0]) / 2, centerY = (origin[1] + end[1]) / 2;
  const notBlocked = (row: Row): boolean => painted ? true : mask !== null
    ? !aoeMaskTargetBlocked(read, mask, wallGateShape, row)
    : !shapeAoeTargetBlocked(read, wallGateShape, centerX, centerY, row);
  // Ordinary stage selections already carry their visible occupants. Painted paths sweep the whole ribbon as well,
  // so a partial stage list cannot lose a crossed creature or object; named occupants retain their order.
  if (explicitIds.length === 0 || painted) {
    const named = new Set(targets.map(row => entityId(row, read)));
    for (const row of creatures) if (!named.has(entityId(row, read)) && casterAllowed(row) && teamAllows(row) && intersects(row) && notBlocked(row)
      && !onlyInventory && creatureFilters(row)) targets.push(row);
    if (!read.has(ability, 'ability_does_not_effect_inventory') && !opposingOnly && !zoneOpposingOnly)
      for (const row of inventories) if (!named.has(entityId(row, read)) && targetItemType(read, row) !== 'map_marker' && entityId(row, read) && intersects(row) && notBlocked(row)) targets.push(row);
  }
  const existing = new Set(targets.map((r) => entityId(r, read)));
  for (const row of creatures) {
    const hidden = read.hidden(row); if (hidden === undefined) handBack('check_target_hidden:scene-state-missing');
    const id = entityId(row, read);
    if (hidden && casterAllowed(row) && !existing.has(id) && teamAllows(row) && intersects(row) && notBlocked(row)
      && creatureFilters(row)) { targets.push(row); existing.add(id); }
  }
  // the hidden prop sweep: skipped wholesale for opposing-only ($fold_hidden_inventory), per prop for an opposing-only zone
  for (const row of opposingOnly || zoneOpposingOnly ? [] : inventories) {
    const hidden = read.hidden(row); if (hidden === undefined) handBack('check_target_hidden:scene-state-missing');
    const id = entityId(row, read);
    if (hidden && targetItemType(read, row) !== 'map_marker' && id && !existing.has(id) && intersects(row) && notBlocked(row)) { targets.push(row); existing.add(id); }
  }
  if (mask !== null && mask.directional === true) orderAlongMaskDirection(read, mask, targets);
  // AUTHORITATIVE opposing-team filter: every non-opposing creature and every prop leaves, however it arrived; the anchor row stays.
  if (opposingOnly && truth(read.value(ability, 'aoe_type'))) {
    // target_arr_is_inventory_target() first (a prop never passes), then only creature rows are judged; the map-point anchor stays
    const kept = targets.filter((row) => read.inventoryField(row, 'inventory_id') ? false
      : !record(row.encounter_creatures)?.encounter_creature_id || opposingTeams(read, caster, row));
    targets.splice(0, targets.length, ...kept);
  }
  if (skipSameTeam) {
    const kept = targets.filter(teamAllows);
    targets.splice(0, targets.length, ...kept);
  }
  // S21: judge each 5 ft section with the same area and propagation gates as the target sweep.
  // A sweep-only run with no covered section leaves; explicitly named targets retain redirect's fallback.
  if (wallSections && !painted) for (const row of targets) {
    if (targetItemType(read, row) !== 'wall') continue;
    const generation = normalizeInventoryGeneration(read.inventoryField(row, 'inventory_generation'));
    const run = record(generation?.wallRun), span = record(run?.span), z = record(run?.z);
    const current = computedValue(row, 'vitality', 'v_current') ?? computedValue(row, 'vitality', 'base_v_current');
    if (!run || !span || !z || Number(span.max) - Number(span.min) <= 5 + 1e-6 || (current != null && Number(current) <= 0)) continue;
    const starts = JSON.parse(String(json_encode(ftWallRunSectionMins(run)))) as number[];
    const covered = starts.filter(start => {
      const sectionEnd = Number(ftWallRunSectionMax(run, start));
      const center = JSON.parse(String(json_encode(ftWallRunPieceCenter(run, start, sectionEnd)))) as { x: number; y: number };
      const section = { ...row, inventory: { ...record(row.inventory), inventory_size: 'DC23AA88-CB3D-EA4E-F6F7-6389ABC4F9DC',
        inventory_generation: generation }, position: { x_pos: center.x, y_pos: center.y, z_pos: rowCoord(read, row, 'z_pos') } };
      return ftShapeAoeIntersectsTarget({ kind: 'circle', x: center.x, y: center.y, r: 2.5,
        zMin: Number(z.min), zMax: Number(z.max) }, shape, radius, depth, height, origin, end, selfCentered) && notBlocked(section);
    });
    wallSections.set(entityId(row, read), covered);
  }
  if (wallSections) {
    const kept = targets.filter(row => explicitIds.includes(entityId(row, read)) || wallSections.get(entityId(row, read))?.length !== 0);
    targets.splice(0, targets.length, ...kept);
  }
  // R-103 targetCountRefusal exempts map-point selections: area occupants are never count-limited.
  if (selectionHeld) return { targets };
  // PHP's empty-area branch (validate_and_adjust_targets §2) first runs the enemy auto-picks and the own-item default
  // (ability_uses_caster_inventory_target_picker needs ability_works_only_against_item_type) — not modelled here.
  if (targets.length === 0 && (['target_all_enemies', 'target_random_enemy', 'target_nearest_enemy', 'target_lowest_vitality_enemy',
    'ability_works_only_against_item_type'].some((key) => read.has(ability, key))))
    handBack('validate_and_adjust_targets:empty-area-auto-target');
  if (targets.length === 0 && upper(read.value(ability, 'range')) === SELF_RANGE && !selfDirectional && !skipSameTeam) targets.push(caster);
  if (targets.length === 0) {
    if (truth(read.value(ability, 'persistent_aoe'))) return { targets: [] };
    const type = String(itemField(ability, 'item_type') ?? '');
    // §4 skips the warning for Spot Hidden / Search for Hidden Inventory and a targetless fabrication.
    if (read.has(ability, 'fabricate') || ['B2D3417B-2700-11CB-E17D-38DECAF66F1A', '5B518ED7-4CEF-72BE-F20B-912598267714'].includes(upper(itemField(ability, 'item_id'))))
      handBack('validate_and_adjust_targets:empty-area-exempt');
    // ucwords_and_space(item_type): every finalizable type is plain lower_snake words.
    if (read.value(ability, 'range') != null && FINALIZABLE_TYPES.has(type))
      return { targets: [], emptyArea: true,
        refusal: `This ${type.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} has a range and requires a target; perhaps target the environment.` };
    handBack('validate_and_adjust_targets:no-target-adjustment');
  }
  return { targets };
}

/** Finish the placed target set before browser dispatch, retaining consumable, range and scene gates. */
export function finalizePlacedAoeTargets(read: PlacedAoeReaders, placed: PlacedAoePlacement, caster: Row, ability: Row,
  wallSections?: Map<string, number[]>): Row[] {
  // The FT3D stage appends the occupants it found (buildFt3dAoeTargetSelection: `<map point>,<id>,…`) whenever the area covers anyone; the
  // expansion takes them as the visible members, as PHP did. Nothing later re-judged them for a player cast: the caster-to-target range
  // check ran only at ONE target and never for a precast-validated map-point area, and the spatial gate skipped player dispatches
  // (validate_and_adjust_targets §5). Handing them back here refused every placed area over a creature (Fog Cloud, playtest 2026-10-13).
  // A persistent zone places its anchor from the same selection (placePersistentAoe); a follow zone picks a carrier instead.
  // janus() swaps attack_ability / opportunity_attack_ability to the weapon BEFORE validation.
  const type = String(itemField(ability, 'item_type') ?? '');
  if (!FINALIZABLE_TYPES.has(type)) handBack('validate_and_adjust_targets:placed-finalization-unported');
  // ft_aoe_placement_failure() is the first veto validate_and_adjust_targets reaches for a lone map token (line
  // ~291) — before the Self aura forcing and every expansion — so a scene-proved refusal stands for any shape.
  const [encounterId, rawCoords, ...extra] = placed.targetId.trim().split('|');
  const coordCount = rawCoords?.split(':').length ?? 0;
  if (placed.paintedWall == null && !extra.length && isEncounterIdentity(encounterId) && (coordCount === 3 || coordCount === 6)) {
    const failure = read.placementFailure(placed.targetId, caster, ability);
    if (typeof failure === 'string') refuse(failure);
  }
  // Every mapped shape is already judged by ftShapeAoeIntersectsTarget in the shared occupancy sweep.
  if (!SHAPES[upper(read.value(ability, 'aoe_type'))]) handBack('validate_and_adjust_targets:unknown-aoe-shape');
  if (truth(read.value(ability, 'aoe_follows_target')))
    handBack('validate_and_adjust_targets:placed-finalization-unported');
  // A persistent aura on a Self range forces [caster] and re-expands around it (check_item_arr_aura_effect +
  // expand_self_follow_aura_targets_if_needed); only reachable with aoe_follows_target, handed back above.
  // The AOE policy in ft_spatial_action_policy() returns area-origin for every aoe_type item, so the
  // later ft_spatial_action_context() direct-target gate returns clear before tracing a segment.
  const expanded = expandPlacedAoeTargets(read, placed, caster, ability, false, false, wallSections);
  // Every refusal the expansion reports is a validation veto PHP issues before any draw: the scene-proved
  // ft_aoe_placement_failure() message (the first veto validate_and_adjust_targets can reach for a lone map
  // token), or the no-target "has a range and requires a target" warning.
  // …except that warning for a placed area: OWNER RULINGS 27 + 28 (2026-10-14, Wizard Guy's Fireball on empty ground) — an area's only gate
  // is its point of contact and it never needs a target: placed on empty ground it goes off and pays its cost, nothing is hit (janus.ts's
  // empty-ground branch). The later gates below still apply to the empty set.
  if (expanded.refusal && !expanded.emptyArea) refuse(expanded.refusal);
  // A single remaining target skips PHP's caster-to-target range check: ft_aoe_placement_failure() set
  // $ft_aoe_map_point_precast_validated for this non-trigger map-point AOE (aoe_type + aoe_distance), and the
  // Self + AOE form measures the caster against itself.
  if (read.has(ability, 'ability_works_only_against_item_type'))
    handBack('validate_and_adjust_targets:allowed-item-types');
  // A consumable with a campaign applies an extra single-target veto. None of
  // its inventory/consume writes are carried by the generic verdict yet.
  if (read.has(ability, 'consumable') || ['potion', 'elixir', 'food', 'drink'].includes(String(itemField(ability, 'item_type') ?? '')))
    handBack('validate_and_adjust_targets:consumable-finalization');
  if (read.value(ability, 'range') == null) handBack('validate_and_adjust_targets:range-finalization');
  return expanded.targets;
}
